/**
 * Presentation helpers for the agent RBAC tier (see Squad `AgentTier`).
 *
 * Governance tier drives the badge + the read-only behaviour: a `system` agent
 * is Skalx-owned and read-only for clients (the backend already returns
 * `canEdit: false` for it, so the existing `agent.canEdit === false` gating
 * disables edit controls — this just labels it and shows a lock).
 */

import type { WorkspaceAgent } from 'src/api/facade/types'

export type AgentTier = NonNullable<WorkspaceAgent['tier']>

/** AntD Tag `color` per tier. */
export const TIER_COLOR: Record<AgentTier, string> = {
  system: 'default',
  template: 'blue',
  workspace: 'green',
  personal: 'gold'
}

/** i18n key per tier — resolved as `workspace.agents.tier.<tier>`. */
export const TIER_LABEL_KEY: Record<AgentTier, string> = {
  system: 'workspace.agents.tier.system',
  template: 'workspace.agents.tier.template',
  workspace: 'workspace.agents.tier.workspace',
  personal: 'workspace.agents.tier.personal'
}

export const tierOf = (agent: Pick<WorkspaceAgent, 'tier'>): AgentTier =>
  agent.tier ?? 'personal'

export const isSystemAgent = (agent: Pick<WorkspaceAgent, 'tier'>): boolean =>
  tierOf(agent) === 'system'

/** A managed/system agent the caller can't edit — show a lock. */
export const isLockedAgent = (
  agent: Pick<WorkspaceAgent, 'tier' | 'canEdit'>
): boolean => isSystemAgent(agent) && agent.canEdit === false
